import { useMemo } from 'react'
import { Box, Button, Paper, Skeleton, Typography, useMediaQuery, useTheme } from '@mui/material'
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { CHART_COLORS } from '@features/admin/components/dashboard/chartConfig'
import type { PerfTimePoint } from '@api/types/performance'
import { formatAxisTime, formatCount, formatDateTime, formatMs } from './format'

interface LatencyChartProps {
  title: string
  points?: PerfTimePoint[]
  hours: number
  loading?: boolean
  /**
   * Set when the series could not be fetched. Rendered instead of the empty state, because the two
   * are not the same thing and conflating them hid a 500 behind the words "nothing recorded yet" —
   * a chart that reports a broken query as an idle period is worse than one that draws nothing.
   */
  error?: boolean
  /** Shown as a Retry button next to the error text. */
  onRetry?: () => void
  /** Adds a second axis with request volume behind the latency lines. */
  showVolume?: boolean
  height?: number
}

/**
 * Latency over time, with request volume behind it.
 *
 * The volume matters as much as the latency. A p95 that doubles at three in the morning on four
 * requests is noise; the same jump under load is the thing you came here for, and without the
 * volume behind the lines the two look identical.
 *
 * The median is drawn alongside the tail on purpose. When the p95 climbs and the p50 does not, some
 * requests are slow. When both climb, everything is slow. Those have different causes and the chart
 * should not need a second view to tell them apart.
 */
export function LatencyChart({
  title,
  points,
  hours,
  loading,
  error,
  onRetry,
  showVolume = true,
  height = 300,
}: LatencyChartProps) {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  // On a phone the chart has ~320px to work with. Rather than forcing a sideways scroll, the axes
  // narrow and the volume axis loses its numbers: the faint area still shows the shape of the load,
  // and the tooltip still gives the exact count.
  const compact = useMediaQuery(theme.breakpoints.down('sm'))

  const data = useMemo(
    () =>
      (points ?? []).map((p) => ({
        ...p,
        label: formatAxisTime(p.bucketUtc, hours),
      })),
    [points, hours]
  )

  const gridColor = isDark ? CHART_COLORS.gridDark : CHART_COLORS.grid
  const axisColor = isDark ? CHART_COLORS.axisDark : CHART_COLORS.axis

  return (
    <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        {title}
      </Typography>

      {loading ? (
        <Skeleton variant="rectangular" height={height} />
      ) : error ? (
        <Box
          sx={{
            height,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            px: 2,
          }}
        >
          <Typography variant="body2" color="error.main" textAlign="center">
            Could not load this chart. The request failed; this is not the same as nothing being recorded.
          </Typography>
          {onRetry && (
            <Button variant="outlined" size="small" onClick={onRetry}>
              Retry
            </Button>
          )}
        </Box>
      ) : data.length === 0 ? (
        <Box sx={{ height, display: 'grid', placeItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Nothing recorded in this window yet.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ width: '100%', minWidth: 0 }}>
          <ResponsiveContainer width="100%" height={height}>
            <ComposedChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis dataKey="label" tick={{ fill: axisColor, fontSize: 11 }} minTickGap={24} />
              <YAxis
                yAxisId="ms"
                tick={{ fill: axisColor, fontSize: 11 }}
                width={compact ? 44 : 54}
                tickFormatter={(v: number) => (v >= 1000 ? `${(v / 1000).toFixed(1)}s` : `${v}ms`)}
              />
              {showVolume && (
                <YAxis
                  yAxisId="count"
                  orientation="right"
                  tick={compact ? false : { fill: axisColor, fontSize: 11 }}
                  width={compact ? 4 : 48}
                />
              )}

              <Tooltip
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 8,
                }}
                labelFormatter={(_label, payload) => {
                  const point = payload?.[0]?.payload as PerfTimePoint | undefined
                  return point ? formatDateTime(point.bucketUtc) : ''
                }}
                formatter={(value: number, name: string) =>
                  name === 'Requests' ? [formatCount(value), name] : [formatMs(value), name]
                }
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />

              {showVolume && (
                // Behind the lines and deliberately faint: it is context for the latency, not a
                // reading of its own.
                <Area
                  yAxisId="count"
                  type="monotone"
                  dataKey="count"
                  name="Requests"
                  stroke={theme.palette.divider}
                  fill={theme.palette.action.hover}
                  strokeWidth={1}
                />
              )}

              <Line
                yAxisId="ms"
                type="monotone"
                dataKey="p50Ms"
                name="p50"
                stroke={CHART_COLORS.newUsers}
                strokeWidth={2}
                dot={false}
              />
              <Line
                yAxisId="ms"
                type="monotone"
                dataKey="p95Ms"
                name="p95"
                stroke={CHART_COLORS.soloCompletions}
                strokeWidth={2}
                dot={false}
              />
              <Line
                yAxisId="ms"
                type="monotone"
                dataKey="p99Ms"
                name="p99"
                stroke={CHART_COLORS.activeUsers}
                strokeWidth={2}
                dot={false}
              />
              <Line
                yAxisId="ms"
                type="monotone"
                dataKey="avgDbMs"
                name="DB (avg)"
                stroke={CHART_COLORS.groupCompletions}
                strokeWidth={1.5}
                strokeDasharray="4 3"
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Paper>
  )
}
